import {
    Avatar,
    Box,
    Collection,
    CollectionItem,
    CollectionLink,
    Divider,
    Drawer,
    Heading,
    Spacer,
    Text,
} from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { IOrganizationGetOne } from '@frontend/components/interfaces';
import { ManagerAuthorization } from '@common/enums';
import { hasOneAuthorization } from '@frontend/utils/hasAuthorization';
import type { IGroupGetOne } from '../../interfaces/groupGetOne';
import { translateKey } from '../../../translate';
import { useAbortableFetch } from '../../../utils/hooks/useAbortableFetch';

interface IGroupSidebarProps {
    groupId: string;
    isVisible: boolean;
    onClose: () => Promise<void>;
}

export function GroupListSidebar(props: IGroupSidebarProps): JSX.Element {
    const navigate = useNavigate();
    const abortableFetch = useAbortableFetch();
    const [group, setGroup] = useState<IGroupGetOne>();
    const { groupId, isVisible, onClose } = props;
    const canReadOrganization = hasOneAuthorization(ManagerAuthorization.organizationRead);

    useEffect(() => {
        if (groupId) {
            loadGroup(groupId);
        }
    }, [groupId]);

    if (!group || !groupId) {
        return <></>;
    }

    return (
        <>
            <Drawer
                isOpen={isVisible}
                onClose={async () => await onClose()}
            >
                <Drawer.Header>{translateKey('groups.group')}</Drawer.Header>
                <Drawer.CloseButton />
                <Drawer.Body>
                    <Collection
                        aria-label="groepen lijst"
                        type="nav"
                    >
                        <CollectionLink
                            alignItems="start"
                            justifyContent="start"
                            mediaItem={
                                <Avatar
                                    icon="organization"
                                    shape="square"
                                    size={40}
                                />
                            }
                            onClick={async () => {
                                await onClose();
                                redirectToGroupPage(groupId);
                            }}
                            padding="small"
                        >
                            <Spacer
                                direction="vertical"
                                spacing="small"
                            >
                                <Box mb={2}>
                                    <Heading size="medium">{group.name}</Heading>
                                </Box>
                                {group.relationNumber && (
                                    <Box mb={1}>
                                        <Text size="sm">
                                            <Text
                                                as="span"
                                                fontWeight="medium"
                                                size="sm"
                                            >
                                                {translateKey('organization.relationNumber')}:
                                            </Text>{' '}
                                            {group.relationNumber}
                                        </Text>
                                    </Box>
                                )}
                                {group.financialEmailAddress && (
                                    <Box>
                                        <Text size="sm">
                                            <Text
                                                as="span"
                                                fontWeight="medium"
                                                size="sm"
                                            >
                                                {translateKey('groups.financialEmailAddress')}:
                                            </Text>{' '}
                                            {group.financialEmailAddress}
                                        </Text>
                                    </Box>
                                )}
                            </Spacer>
                        </CollectionLink>
                    </Collection>
                    <Divider color="minimal" />

                    {group.organizations && group.organizations.length > 0 && (
                        <Collection
                            aria-label="organisatie lijst"
                            type={canReadOrganization ? 'nav' : 'ul'}
                        >
                            <Heading
                                mb={6}
                                mt={8}
                                size="medium"
                            >
                                {translateKey('organization.organizations')}
                            </Heading>
                            {group.organizations && group.organizations.map((item, i) => renderOrganization(item, i))}
                        </Collection>
                    )}
                </Drawer.Body>
            </Drawer>
        </>
    );

    function redirectToOrganizationPage(organizationId: string) {
        navigate(`/organizations/${organizationId}/view`);
    }

    function redirectToGroupPage(groupIdentifier: string) {
        navigate(`/groups/${groupIdentifier}`);
    }

    async function loadGroup(groupIdentifier: string) {
        const url = `/api/groups/${groupIdentifier}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setGroup(result.json);
    }

    function renderOrganization(organization: IOrganizationGetOne, index: number) {
        return canReadOrganization ? renderLinkItem(organization, index) : renderItem(organization, index);
    }

    function renderItem(organization: IOrganizationGetOne, index: number) {
        return (
            <CollectionItem
                key={index}
                mediaItem={
                    <Avatar
                        icon="organization"
                        shape="square"
                        size={40}
                    />
                }
                padding="small"
                subtitle={organization.txcsId}
                title={organization.name}
            />
        );
    }

    function renderLinkItem(organization: IOrganizationGetOne, index: number) {
        return (
            <CollectionLink
                key={index}
                mediaItem={
                    <Avatar
                        icon="organization"
                        shape="square"
                        size={40}
                    />
                }
                onClick={async () => {
                    await onClose();
                    redirectToOrganizationPage(organization.id);
                }}
                padding="small"
                subtitle={organization.txcsId}
                title={organization.name}
            ></CollectionLink>
        );
    }
}
